import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { getMe, switchAccount as switchAccountApi } from '../api/auth'
import toast from 'react-hot-toast'

const AuthContext = createContext(null)

// Role constants
export const ROLES = {
  ADMIN: 'admin',
  COORDINATOR: 'coordinator',
  SENIOR_RECEIVER: 'senior_receiver',
  RECEIVER: 'receiver',
  WAREHOUSE_WORKER: 'warehouse_worker',
  PLACER: 'placer',
  CLIENT: 'client'
}

// Account type constants
export const ACCOUNT_TYPES = {
  WAREHOUSE: 'warehouse',
  CLIENT: 'client'
}

// Role display names
export const ROLE_NAMES = {
  [ROLES.ADMIN]: 'Администратор',
  [ROLES.COORDINATOR]: 'Координатор',
  [ROLES.SENIOR_RECEIVER]: 'Старший приёмщик',
  [ROLES.RECEIVER]: 'Приемщик',
  [ROLES.WAREHOUSE_WORKER]: 'Кладовщик',
  [ROLES.PLACER]: 'Размещение',
  [ROLES.CLIENT]: 'Клиент'
}

/**
 * Get display name for a set of roles.
 * Special case: warehouse_worker + placer = "Кладовщик+"
 */
export function getRoleDisplayName(roles) {
  if (!roles || roles.length === 0) return ''
  if (roles.length === 1) return ROLE_NAMES[roles[0]] || roles[0]

  // Special combo: warehouse_worker + placer = "Кладовщик+"
  const hasWorker = roles.includes(ROLES.WAREHOUSE_WORKER)
  const hasPlacer = roles.includes(ROLES.PLACER)
  if (hasWorker && hasPlacer && roles.length === 2) {
    return 'Кладовщик+'
  }

  return roles.map(r => ROLE_NAMES[r] || r).join(', ')
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      getMe()
        .then(setUser)
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  const login = (token, userData) => {
    localStorage.setItem('token', token)
    setUser(userData)
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('original_token')
    setUser(null)
  }

  // Impersonate another user (superadmin only)
  const impersonate = useCallback(async (userId) => {
    try {
      const { impersonateUser } = await import('../api/superadmin')
      const { access_token } = await impersonateUser(userId)
      localStorage.setItem('original_token', localStorage.getItem('token'))
      localStorage.setItem('token', access_token)
      window.location.reload()
    } catch (error) {
      toast.error('Ошибка при входе за пользователя')
    }
  }, [])

  // Return to superadmin's own session
  const stopImpersonating = useCallback(() => {
    const originalToken = localStorage.getItem('original_token')
    if (originalToken) {
      localStorage.setItem('token', originalToken)
      localStorage.removeItem('original_token')
      window.location.reload()
    }
  }, [])

  // Switch to a different account
  const switchAccount = useCallback(async (accountId) => {
    try {
      const { access_token } = await switchAccountApi(accountId)
      localStorage.setItem('token', access_token)
      window.location.reload()
    } catch (error) {
      toast.error('Ошибка при переключении аккаунта')
    }
  }, [])

  // Check if user has one of the allowed roles (M2M)
  const hasRole = useCallback((...allowedRoles) => {
    if (!user || !user.roles) return false
    return user.roles.some(r => allowedRoles.includes(r))
  }, [user])

  // Single useMemo for all derived values — avoids 10 separate memo overhead
  const contextValue = useMemo(() => {
    const roles = user?.roles || []
    const accountType = user?.account?.account_type
    const accounts = user?.accounts || []

    return {
      user,
      login,
      logout,
      loading,
      hasRole,
      switchAccount,
      impersonate,
      stopImpersonating,

      // Superadmin
      isSuperadmin: user?.is_superadmin === true,
      isImpersonating: !!localStorage.getItem('original_token'),

      // Role checks (computed once per user change)
      isAdmin: roles.includes(ROLES.ADMIN),
      isAdminOrCoordinator: roles.some(r => [ROLES.ADMIN, ROLES.COORDINATOR].includes(r)),
      canCreateOrders: roles.some(r => [ROLES.ADMIN, ROLES.COORDINATOR, ROLES.CLIENT].includes(r)),
      canEditProducts: roles.some(r => [ROLES.ADMIN, ROLES.COORDINATOR, ROLES.SENIOR_RECEIVER, ROLES.CLIENT].includes(r)),
      canEditDimensions: roles.some(r => [ROLES.ADMIN, ROLES.RECEIVER, ROLES.SENIOR_RECEIVER].includes(r)),
      canApproveDimensions: roles.some(r => [ROLES.ADMIN, ROLES.COORDINATOR, ROLES.SENIOR_RECEIVER].includes(r)),
      isInternalUser: roles.some(r => r !== ROLES.CLIENT),
      isClient: roles.length === 1 && roles[0] === ROLES.CLIENT,

      // Account type checks
      isWarehouseAccount: accountType === 'warehouse',
      isClientAccount: accountType === 'client',

      // Multi-account
      accounts,
      activeAccountId: user?.active_account_id || user?.account_id,
      hasMultipleAccounts: accounts.length > 1,
    }
  }, [user, loading, hasRole, switchAccount, impersonate, stopImpersonating])

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
